import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_ROOM_I18N_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type ProductRoomI18nFormModel } from "./type";
import { roomI18nInputZodSchema, type RoomI18nInputZodSchemaType } from "./validation-schema";

import type { ProductRoomI18nFormModelContextType } from "../ui/_context/context";

type Props = {
	inputModel: ProductRoomI18nFormModel["InputModel"];
	queryData: ProductRoomI18nFormModel["QueryData"];
};

export const useInitProductRoomI18nFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<ProductRoomI18nFormModelContextType, "queryData"> => {
	const hotelID = _inputModel.hotelID;
	const roomID = _inputModel.roomID;
	const lang = _inputModel.lang;

	const defaultInputModel = DEFAULT_PRODUCT_ROOM_I18N_INPUT_MODEL({ roomID, hotelID, queryData, lang });
	const initInput = _inputModel ?? defaultInputModel;
	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			ProductRoomI18nFormModel["InputModel"],
			ExcludeKeys,
			ProductRoomI18nFormModel["QueryData"],
			RoomI18nInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput,
			currentInput: initInput,
			excludeKeys: excludeKeys,
			zodSchema: roomI18nInputZodSchema,
		}),
	);

	const onSetViewModel: ProductRoomI18nFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as ProductRoomI18nFormModel["ViewModel"],
				),
			} satisfies ProductRoomI18nFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				ProductRoomI18nFormModel["InputModel"],
				ExcludeKeys,
				ProductRoomI18nFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				ProductRoomI18nFormModel["InputModel"],
				ExcludeKeys,
				ProductRoomI18nFormModel["QueryData"],
				RoomI18nInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: roomI18nInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, initInput, viewModel],
	);

	return {
		viewModel,
		onSetViewModel,
	};
};
